/**
 * Helpers for showing class sessions on the appointment calendar.
 *
 * A class session is an ordinary `appointment` row with `type: "CLASS"`, so it
 * arrives through the same calendar endpoints as everything else — the list
 * payload carries a `class` object (name, coach, capacity, booked_count) so a
 * tile can render without an extra request.
 *
 * A recurring class also puts *projected* tiles on the calendar: dates its rule
 * produces that no row exists for yet. They carry `is_virtual: true`, a
 * `v:<class>:<date>` id and null billing fields, and they hold the court just
 * as firmly as a real booking — so they are drawn distinctly but never treated
 * as free time.
 */

export const CLASS_EVENT_COLORS = {
  backgroundColor: "#ede9fe",
  borderColor: "#7c3aed",
  textColor: "#4c1d95",
};

/** Projected tiles read as lighter and dashed so they aren't mistaken for booked rows. */
export const VIRTUAL_CLASS_EVENT_COLORS = {
  backgroundColor: "#f5f0ff",
  borderColor: "#b794f4",
  textColor: "#553c9a",
};

export const isClassAppointment = (appt: any): boolean =>
  String(appt?.type || "").toUpperCase() === "CLASS" || Boolean(appt?.class);

/** True while the tile is only projected from the class's recurrence rule. */
export const isVirtualClassAppointment = (appt: any): boolean =>
  appt?.is_virtual === true || String(appt?.id ?? "").startsWith("v:");

/** True for a calendar event object (as opposed to a raw appointment row). */
export const isClassEvent = (event: any): boolean =>
  Boolean(event?.extendedProps?.isClass) ||
  isClassAppointment(event?.extendedProps?.raw);

export const isVirtualClassEvent = (event: any): boolean =>
  Boolean(event?.extendedProps?.isVirtualClass) ||
  isVirtualClassAppointment(event?.extendedProps?.raw);

export const getClassEventTitle = (appt: any): string =>
  appt?.class?.name || "Class";

/** Secondary tile line: "Coach Jane • 4/10". */
export const getClassEventSubtitle = (appt: any): string => {
  const parts: string[] = [];
  const coachName = appt?.class?.coach?.full_name;
  if (coachName) parts.push(coachName);

  // booked_count sits on the tile itself for a projected session (there is no
  // roster row to count) and under `class` for a materialized one.
  const booked =
    typeof appt?.booked_count === "number"
      ? appt.booked_count
      : appt?.class?.booked_count;
  const capacity = appt?.class?.capacity;
  if (typeof booked === "number") {
    parts.push(capacity ? `${booked}/${capacity}` : `${booked} booked`);
  }

  if (isVirtualClassAppointment(appt)) parts.push("projected");

  return parts.join(" • ");
};

/** extendedProps added to a class session's calendar event. */
export const buildClassEventProps = (appt: any) => ({
  isClass: true,
  isVirtualClass: isVirtualClassAppointment(appt),
  classId: appt?.class?.id ? String(appt.class.id) : undefined,
  className: getClassEventTitle(appt),
  classLine: getClassEventSubtitle(appt),
});

/**
 * A slot from `GET /appointments/unit/:id/unavailable-slots` that a class is
 * sitting on. It is a third source alongside APPOINTMENT and BLOCK — anything
 * that only skips those two will happily offer a time the class already uses,
 * which is a real double-booking rather than a cosmetic bug.
 */
export const isClassUnavailableSlot = (slot: any): boolean =>
  String(slot?.source || "").toUpperCase() === "CLASS";
